import React, { useState, useEffect, useRef, useCallback } from "react";
import classes from "./style.module.scss";
import { API } from "app/routes";
import { useAppHeader } from "~/app/modules/appHeader";
import usePageTitle from "~/app/hooks/usePageTitle";
import AccessControl from "~/accessControl";
import Button from "~/app/common/Button";
import Icon from "~/app/common/Icon";
import SearchInput from '~/app/modules/form/Search';
import { useHistory } from "react-router-dom";
import { PROJECTS_ROUTES } from "~/projects/Router";
import { PERMISSION_GROUPS } from "~/accessControl/permissionGroups";
import { TIME_MANAGEMENT_ROUTES } from "../Router";
import { route } from "~/app/helpers/route";
import { TASK_ROUTES } from "~/tasks/Router";
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreState, useStoreActions } from "~/store";
import useAccessControl from "~/app/hooks/useAccessControl";
import { Project, MachineSearch, IssueResponsePayload } from "~/types";
import { notify } from "~/app/helpers/notify";
import { generateGUID } from '~/app/helpers/guid';
import AddIssue from '~/issues/Add';
import { SOURCE_ISSUE_TYPE } from '~/app/data/statuses/issue';
import { formatMachineSearchLabel, formatMachineSearchValue } from "~/machines/helpers";
import { PERIODIC_CHECK_ITEM_NOT_APPLICABLE } from '~/app/data/statuses/periodicCheck';
import { machineRepository } from '~/data/repositories';
import { connectivityService } from '~/data/connectivity/ConnectivityService';
import moment from 'moment';
import { isPeriodicCheckAwaitingSync, usePendingPeriodicChecks } from '~/periodicCheck/usePendingPeriodicChecks';

interface Activity {
  id: number;
  name: string;
  type?: number | null;
  status: number;
  absence_type?: string | null;
}

interface PeriodicCheck {
  id: number;
  name: string;
  type?: string;
  isRequired?: boolean;
  status?: number;
  isImageRequired?: boolean;
  comment?: string;
  groupId?: number;
  fluidTypeId?: number;
}

interface ChecksData {
  inspectionType: number;
  checks: PeriodicCheck[];
  lastRegisteredCheck?: any;
}

export interface PeriodicCheckPayload extends PeriodicCheck {
  status: 0 | 1 | 2 | 3 | null
  quantity: number
  issues: IssueResponsePayload[]
  images?: (string | File)[]
  im?: {
    dc?: string
    sz?: number
  }[]
  sync?: number
}

const ProjectSearchWrapper: React.FC<{
  forceUpdate: number;
  projectId?: number | string;
  onSelect: (item: Project) => void;
  t: (key: string) => string;
  classes: any;
}> = ({ forceUpdate, projectId, onSelect, t, classes }) => {
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, [forceUpdate]);

  const handleSelect = useCallback((item: Project) => {
    if (mountedRef.current) {
      onSelect(item);
    }
  }, [onSelect]);

  return (
    <SearchInput<Project>
      key={`project-${forceUpdate}-${projectId || 'empty'}`}
      placeholder={t('projectPlaceholder')}
      className={classes['searchField']}
      inputClassName={classes['searchInput']}
      searchUrl={API.projects}
      renderItem={(item) => {
        if (item && 'result' in item && item.result === 'fail') {
          return '';
        }

        if (item && item.number !== undefined && item.name !== undefined) {
          const number = String(item.number || '');
          const name = String(item.name || '');
          return `${number} ${name}`.trim();
        }

        return '';
      }}
      onSelect={handleSelect as any}
      iconPosition="left"
      defaultValue={projectId}
    />
  );
};

const MachineSearchWrapper: React.FC<{
  machineForceUpdate: number;
  selectedMode: string | null;
  onSelect: (item: MachineSearch) => void;
  t: (key: string) => string;
  classes: any;
}> = ({ machineForceUpdate, selectedMode, onSelect, t, classes }) => {
  const mountedRef = useRef(true);
  const [machineSearchUrl, setMachineSearchUrl] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    setMachineSearchUrl(`${API.machines}?t=${Date.now()}`);
    return () => {
      mountedRef.current = false;
    };
  }, [machineForceUpdate, selectedMode]);

  const handleSelect = useCallback((item: MachineSearch) => {
    if (mountedRef.current) {
      onSelect(item);
    }
  }, [onSelect]);

  if (!machineSearchUrl) return null;

  return (
    <SearchInput<MachineSearch>
      key={`machine-search-${machineForceUpdate}-${selectedMode}-${machineSearchUrl}`}
      placeholder={t('search')}
      className={classes.field}
      inputClassName={classes.input}
      searchUrl={machineSearchUrl}
      renderItem={(item) => {
        if (item && 'result' in item && item.result === 'fail') {
          return '';
        }
        if (item && item.intern !== undefined && item.name !== undefined) {
          return formatMachineSearchLabel(item);
        }
        return '';
      }}
      renderValue={formatMachineSearchValue}
      searchKeys={["intern", "name", "regNo", "serialNo", "assignedDriverName"]}
      onSelect={handleSelect as any}
      iconPosition="left"
    />
  );
};

const List: React.FC = () => {
  const { t } = useTranslation('timeManagement');
  const { t: tOffline } = useTranslation('offline');
  const { t: tb } = useTranslation('bottomNav');
  const history = useHistory();

  const [project, setProject] = useState<Project | null>(null);
  const [showError, setShowError] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [showProjectSelection] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showChecksModal, setShowChecksModal] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [machine, setMachine] = useState<MachineSearch | null>(null);
  const [machineForceUpdate, setMachineForceUpdate] = useState(0);
  const [showMachineSearch, setShowMachineSearch] = useState(true);
  const [periodicChecks, setPeriodicChecks] = useState<PeriodicCheckPayload[]>([]);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [checksData, setChecksData] = useState<ChecksData | null>(null);
  const [selectedChecks, setSelectedChecks] = useState<number[]>([]);
  const [isProjectReadonly, setIsProjectReadonly] = useState(false);
  const [unspecifiedNotes, setUnspecifiedNotes] = useState('');
  const [showUnspecifiedModal, setShowUnspecifiedModal] = useState(false);
  const [isModeReadonly, setIsModeReadonly] = useState(false);
  const [openAddIssueDialog, setOpenAddIssueDialog] = useState<boolean>(false);
  const [currentCheck, setCurrentCheck] = useState<PeriodicCheckPayload | null>(null);
  const [issueDetailsForDialog, setIssueDetailsForDialog] = useState<any>(null);
  const [previousIssuesCount, setPreviousIssuesCount] = useState<number>(0);
  const [hasActiveEntry, setHasActiveEntry] = useState(false);
  const isMountedRef = useRef(true);
  const hasInitializedPreselectionsRef = useRef(false);
  const completedChecksCount = React.useMemo(() => {
    return periodicChecks.filter(check => check.status === 1 || check.status === 2 || check.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE).length;
  }, [periodicChecks]);
  const { setData, clearData } = useAppHeader();
  const { checkPermission } = useAccessControl();
  const oppgaverPermission = checkPermission(PERMISSION_GROUPS.oppgaver);
  const timeManagementPermission = checkPermission(PERMISSION_GROUPS.timeManagement);
  const hasTimeforingModule = useStoreState(state => state.tenant.hasTimeforingModule);
  const currentUserName = useStoreState(state => state.user.data.name);
  const getAllActivitiesData = useStoreActions(actions => actions.timeManagement.getAllActivitiesData);
  const getActiveEntry = useStoreActions(actions => (actions as any).timeManagement.getActiveEntry); // ✅ ADDED
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const updatePeriodicChecks = useStoreActions(actions => actions.periodicCheck.update);
  const applyLocalLastPeriodicCheck = useStoreActions(actions => actions.machine.applyLocalLastPeriodicCheck);
  const applyLocalPeriodicCheckChecks = useStoreActions(actions => actions.machine.applyLocalPeriodicCheckChecks);
  const currentUser = useStoreState(state => state.user.data);
  const periodicCheckPermission = checkPermission(PERMISSION_GROUPS.timeManagementPeriodicCheck);
  const minimumRequiredItems = useStoreState(state => state.machines?.minimumRequiredItems);
  const setMachineData = useStoreActions(actions => actions.machine.fetchedMachine);
  const machineIssues = useStoreState(state => state.machine.data.issues || []);
  const inspectionTypeForPending = checksData?.inspectionType ?? 0;
  const { pending: pendingChecks, loaded: pendingChecksLoaded } = usePendingPeriodicChecks(
    machine?.id ? [{ id: machine.id, inspectionType: inspectionTypeForPending }] : [],
    `${machine?.id ?? ''}:${inspectionTypeForPending}`,
  );
  const periodicCheckAwaitingSync = !!machine?.id && isPeriodicCheckAwaitingSync(
    pendingChecks,
    pendingChecksLoaded,
    machine.id,
    inspectionTypeForPending,
  );

  usePageTitle(t('pageTitle'));

  useEffect(() => {
    isMountedRef.current = true;
    setShowMachineSearch(true);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const checkActiveEntry = async () => {
      try {
        const response = await getActiveEntry();
        if (!isMountedRef.current) return;
        if (response?.data) {
          const entries = Array.isArray(response.data)
            ? response.data
            : (response.data.entries || []);
          const activeExists = entries.some((e: any) => e.status === 2);
          setHasActiveEntry(activeExists);
        }
      } catch {
        setHasActiveEntry(false);
      }
    };
    checkActiveEntry();
  }, [getActiveEntry]);

  useEffect(() => {
    if (!currentCheck) {
      setPreviousIssuesCount(machineIssues?.length || 0);
      return;
    }

    if (!machineIssues || machineIssues.length === 0) {
      setPreviousIssuesCount(0);
      return;
    }

    if (machineIssues.length > previousIssuesCount) {
      const newIssue = machineIssues[0];
      setPeriodicChecks(prev =>
        prev.map(check => {
          if (check.id === currentCheck.id) {
            const issueExists = check.issues?.some(i => i.id === newIssue.id);

            if (issueExists) {
              return check;
            }

            const issueToAdd = {
              success: true,
              id: newIssue.id,
              type: newIssue.type || 2
            };

            return {
              ...check,
              issues: [...(check.issues || []), issueToAdd]
            };
          }
          return check;
        })
      );

      setPreviousIssuesCount(machineIssues.length);
    }
  }, [machineIssues, currentCheck, previousIssuesCount]);

  useEffect(() => {
    setData({
      title: t('pageTitle'),
      subTitle: '',
      backLink: undefined,
      isTimeManagementPage: true,
      showUser: false,
      machineId: '',
      projectId: '',
      parentFolderId: 0,
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      isAddTimeForm: false,
    });

    return () => {
      clearData();
    };
  }, [setData, clearData, t]);

  useEffect(() => {
    if (hasInitializedPreselectionsRef.current) return;
    if (!isMountedRef.current) return;

    const locationState = history.location.state as any;

    if (locationState?.fromActiveTime && locationState?.preSelectedProjectId) {
      hasInitializedPreselectionsRef.current = true;

      setIsProjectReadonly(true);

      if (locationState?.preSelectedProject) {
        if (isMountedRef.current) {
          setProject(locationState.preSelectedProject);
          setForceUpdate(prev => prev + 1);
        }
      } else {
        const fetchProjectDetails = async () => {
          try {
            const response = await fetch(`${API.projects}/${locationState.preSelectedProjectId}`);

            if (!isMountedRef.current) return;

            const projectData = await response.json();

            if (isMountedRef.current && projectData) {
              setProject(projectData);
              setForceUpdate(prev => prev + 1);
            }
          } catch (error) {
            if (!isMountedRef.current) return;

            console.error('Error fetching project details:', error);
            if (isMountedRef.current) {
              setProject({
                id: locationState.preSelectedProjectId,
                number: '',
                name: 'Loading...'
              } as Project);
              setForceUpdate(prev => prev + 1);
            }
          }
        };

        fetchProjectDetails();
      }

      if (locationState?.preSelectedMode === 'maskin' && isMountedRef.current) {
        setIsModeReadonly(true);
        setSelectedMode('maskin');
      }
    }

    return () => {
      if (!isMountedRef.current) {
        hasInitializedPreselectionsRef.current = false;
      }
    };
  }, [history.location.state]);

  const fetchPeriodicChecks = useCallback(async (machineId: number) => {
    if (!isMountedRef.current) return;

    setLoadingChecks(true);
    try {
      const machineData = await machineRepository.getMachine(machineId);

      if (!isMountedRef.current) return;

      const inspectionType = machineData?.inspectionType ?? 0;
      const checksArray: PeriodicCheck[] = Array.isArray(machineData?.checks)
        ? machineData.checks
        : [];

      const checksWithPayload: PeriodicCheckPayload[] = checksArray.map(check => ({
        ...check,
        status: 0,
        issues: [],
        quantity: 0,
        images: []
      }));

      const data: ChecksData = {
        inspectionType: inspectionType,
        checks: checksArray,
        lastRegisteredCheck: machineData?.lastDailyCheck
      };

      setChecksData(data);
      setPeriodicChecks(checksWithPayload);

      if (machineData && project) {
        const machineWithProject = {
          ...machineData,
          projectId: Number(project.id),
          id: Number(machineData.id)
        };

        setMachineData(machineWithProject);
      }
    } catch (error) {
      console.error("Error fetching periodic checks:", error);
      setPeriodicChecks([]);
      setChecksData(null);
    } finally {
      if (isMountedRef.current) {
        setLoadingChecks(false);
      }
    }
  }, [project, setMachineData]);

  const fetchActivities = useCallback(async (modeFilter?: string) => {
    if (!isMountedRef.current) return;

    setLoadingActivities(true);
    try {
      const response = await getAllActivitiesData({});

      if (!isMountedRef.current) return;

      let activitiesData: Activity[] = [];
      if (response.data && Array.isArray(response.data)) {
        activitiesData = response.data as Activity[];
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        activitiesData = response.data.data as Activity[];
      } else if (Array.isArray(response)) {
        activitiesData = response as Activity[];
      }
      const currentMode = modeFilter || selectedMode;
      let filteredActivities = activitiesData.filter((activity: Activity) => {
        if (activity.status !== 1) {
          return false;
        }
        if (activity.absence_type != null && String(activity.absence_type).trim() !== '') {
          return false;
        }
        if (activity.type === null || activity.type === undefined) {
          return false;
        }

        if (currentMode === 'maskin') {
          const match = activity.type === 0 || activity.type === 2;
          return match;
        }
        else if (currentMode === 'oppgave') {
          const match = activity.type === 1 || activity.type === 2;
          return match;
        }

        return false;
      });

      setActivities(filteredActivities);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      if (isMountedRef.current) {
        setLoadingActivities(false);
      }
    }
  }, [getAllActivitiesData, selectedMode]);

  const savePeriodicChecks = useCallback(async () => {
    if (!machine || !project || !checksData) return false;

    try {
      const updatedChecks = periodicChecks.map(check => {
        const checkData = {
          id: check.id,
          name: check.name,
          type: check.type,
          isImageRequired: check.isImageRequired,
          comment: check.comment || '',
          groupId: check.groupId,
          fluidTypeId: check.fluidTypeId,
          isRequired: check.isRequired,
          status: check.status,
          quantity: check.quantity || 0,
          issues: check.issues || [],
          images: check.images || [],
          im: check.im || []
        };
        return checkData;
      });

      const queuedOffline = !connectivityService.isOnline() || !!netWorkStatus;
      const response = await updatePeriodicChecks({
        machineId: machine.id,
        projectId: project.id,
        inspectionType: checksData.inspectionType,
        checks: updatedChecks as any,
        guid: generateGUID(),
        isDraft: 0,
      });
      if (response?.queued) {
        const lastCheck = {
          id: `local-${machine.id}`,
          userId: currentUser.id,
          userName: currentUser.name,
          date: moment().format('YYYY-MM-DD'),
          pendingSync: true,
        };
        applyLocalPeriodicCheckChecks({
          machineId: machine.id,
          inspectionType: checksData.inspectionType,
          checks: updatedChecks as any,
          lastCheck,
          projectId: project.id,
        });
        applyLocalLastPeriodicCheck({
          machineId: machine.id,
          inspectionType: checksData.inspectionType,
          lastCheck,
        });
      }
      notify(
        t(queuedOffline ? 'checksSavedOffline' : 'checksSaved') ||
          (queuedOffline ? 'Checks saved offline' : 'Checks saved successfully'),
        'success'
      );

      return true;
    } catch (error) {
      console.error("Error saving periodic checks:", error);
      notify(t('checksSaveError') || 'Error saving checks', 'error');
      return false;
    }
  }, [machine, project, checksData, periodicChecks, netWorkStatus, updatePeriodicChecks, applyLocalLastPeriodicCheck, applyLocalPeriodicCheckChecks, currentUser.id, currentUser.name, t]);

  const handleAddIssueClose = useCallback(() => {
    setOpenAddIssueDialog(false);
    setShowChecksModal(true);
    setTimeout(() => {
      setCurrentCheck(null);
      setIssueDetailsForDialog(null);
    }, 2000);
  }, []);

  const handleStatusChange = useCallback((checkId: number, newStatus: 0 | 1 | 2 | 3) => {
    setPeriodicChecks(prev =>
      prev.map(check => {
        if (check.id === checkId) {
          const updatedStatus: 0 | 1 | 2 | 3 | null = check.status === newStatus ? null : newStatus;

          if (newStatus === 2 && check.status !== 2) {
            setCurrentCheck(check);

            if (machine && project) {
              const issueDetails = {
                machineId: String(machine.id),
                projectId: String(project.id)
              };

              setIssueDetailsForDialog(issueDetails);
            }

            setShowChecksModal(false);
            setTimeout(() => {
              setOpenAddIssueDialog(true);
            }, 100);
            return { ...check, status: 2 as const };
          }

          return { ...check, status: updatedStatus };
        }
        return check;
      })
    );

    if (newStatus === 1) {
      setSelectedChecks(prev => {
        const isCurrentlySelected = prev.includes(checkId);
        if (isCurrentlySelected) {
          return prev.filter(id => id !== checkId);
        } else {
          return [...prev, checkId];
        }
      });
    }
  }, [machine, project]);

  const handleTimeRegistration = () => {
    if (!isMountedRef.current) return;

    if (!project) {
      setShowError(true);
    } else if (!selectedMode) {
      setShowError(true);
    } else if (selectedMode === 'oppgave' && !selectedActivity) {
      setShowError(true);
    } else if (selectedMode === 'maskin' && (!machine || !selectedActivity)) {
      setShowError(true);
    } else {
      setShowError(false);

      let modeTypeValue;
      if (selectedMode === 'maskin') {
        modeTypeValue = 0;
      } else if (selectedMode === 'oppgave') {
        modeTypeValue = 1;
      } else if (selectedMode === 'uspesifisert') {
        modeTypeValue = 2;
      }
      const locationState = history.location.state as any;
      const fromActiveTime = locationState?.fromActiveTime === true;

      if (fromActiveTime) {
        history.push({
          pathname: route(TIME_MANAGEMENT_ROUTES.activeTime),
          state: {
            fromList: true,
            newEntryData: {
              projectId: Number(project.id),
              machineId: machine?.id ? Number(machine.id) : null,
              activityId: selectedActivity?.id ? Number(selectedActivity.id) : null,
              modeType: modeTypeValue,
              notes: selectedMode === 'uspesifisert' ? unspecifiedNotes : undefined,
              checks: selectedMode === 'maskin' ? periodicChecks.filter(c => selectedChecks.includes(c.id)) : []
            }
          }
        });
      } else {
        history.push({
          pathname: route(TIME_MANAGEMENT_ROUTES.startTime),
          state: {
            project,
            mode: selectedMode,
            activity: selectedActivity,
            machine: machine,
            projectId: project.id,
            activityId: selectedActivity?.id || null,
            machineId: machine?.id || null,
            modeType: modeTypeValue,
            notes: selectedMode === 'uspesifisert' ? unspecifiedNotes : undefined,
            checks: selectedMode === 'maskin' ? periodicChecks.filter(c => selectedChecks.includes(c.id)) : []
          }
        });
      }
    }
  };

  const handleProjectSelect = (item: Project) => {
    if (!isMountedRef.current) return;
    if (isProjectReadonly) return;
    setProject(item);
    setForceUpdate(prev => prev + 1);
  };

  const handleModeSelect = async (mode: string) => {
    if (!isMountedRef.current) return;
    if (isModeReadonly) return;

    if (selectedMode === mode) {
      if (mode === 'maskin') {
        setShowMachineSearch(false);
        setMachine(null);
        setTimeout(() => {
          if (isMountedRef.current) {
            setShowMachineSearch(true);
            setMachineForceUpdate(prev => prev + 1);
          }
        }, 50);
      } else if (mode === 'oppgave') {
        await fetchActivities(mode);
        if (!isMountedRef.current) return;
        setShowActivityModal(true);
      } else if (mode === 'uspesifisert') {
        setUnspecifiedNotes('');
        setShowUnspecifiedModal(true);
      }
      return;
    }

    setMachine(null);
    setSelectedActivity(null);
    setSelectedChecks([]);

    setSelectedMode(mode);
    setShowError(false);

    if (mode === 'oppgave') {
      await fetchActivities(mode);
      if (!isMountedRef.current) return;
      setShowActivityModal(true);
    }

    if (mode === 'uspesifisert') {
      setUnspecifiedNotes('');
      setShowUnspecifiedModal(true);
    }
  };

  const handleMachineSelect = useCallback(async (item: MachineSearch) => {
    if (!isMountedRef.current) return;

    setMachine(item);
    setMachineForceUpdate(prev => prev + 1);
    setSelectedChecks([]);

    if (item.id) {
      setLoadingChecks(true);
      await fetchPeriodicChecks(Number(item.id));
      if (!isMountedRef.current) return;
      setShowChecksModal(true);
    }
  }, [fetchPeriodicChecks]);

  const handleChecksConfirm = async () => {
    if (!isMountedRef.current) return;
    if (periodicChecks.length === 0) {
      setShowChecksModal(false);
      await fetchActivities('maskin');
      if (!isMountedRef.current) return;
      setShowActivityModal(true);
      return;
    }

    const requiredMinimum = Math.min(minimumRequiredItems, periodicChecks.length);
    const completedCount = periodicChecks.filter(check => check.status === 1 || check.status === 2 || check.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE).length;

    if (completedCount < requiredMinimum) {
      return;
    }

    const saved = await savePeriodicChecks();

    if (!saved || !isMountedRef.current) {
      return;
    }

    setShowChecksModal(false);

    await fetchActivities('maskin');
    if (!isMountedRef.current) return;
    setShowActivityModal(true);
  };

  const handleActivitySelect = (activity: Activity) => {
    if (!isMountedRef.current) return;
    setSelectedActivity(activity);
  };

  const closeActivityModal = () => {
    if (!isMountedRef.current) return;
    setShowActivityModal(false);
    if (!selectedActivity && !isModeReadonly) {
      setSelectedMode(null);
      setMachine(null);
    }
  };

  const closeChecksModal = () => {
    if (!isMountedRef.current) return;
    setShowChecksModal(false);
    if (!isModeReadonly) {
      setSelectedMode(null);
      setMachine(null);
    }
  };

  const handleImageUpload = (checkId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = Array.from(target.files || []);

      if (files.length > 0) {
        setPeriodicChecks(prev =>
          prev.map(check => {
            if (check.id === checkId) {
              return {
                ...check,
                images: [...(check.images || []), ...files]
              };
            }
            return check;
          })
        );

        notify(t('imagesAdded') || `${files.length} image(s) added`, 'success');
      }
    };

    input.click();
  };

  return (
    <>
      <div className={classes['summaryHeader']}>
        <h2 className={classes['summaryTitle']}>{t('checkSummary')}</h2>
      </div>

      {hasActiveEntry && (
        <div
          onClick={() => history.push(route(TIME_MANAGEMENT_ROUTES.activeTime))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#E8F5E9',
            border: '1px solid #4CAF50',
            borderRadius: '10px',
            padding: '12px 16px',
            margin: '0 12px 16px 12px',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>▶</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#2E7D32' }}>
                {t('activeTimerRunning') || 'Active timer running'}
              </div>
              <div style={{ fontSize: '12px', color: '#388E3C' }}>
                {t('tapToGoToActiveTime') || 'Tap to go to active time'}
              </div>
            </div>
          </div>
          <span style={{ fontSize: '18px', color: '#4CAF50' }}>→</span>
        </div>
      )}

      {!project && (
        <div className={classes['errorMessageStatic']}>
          {t('selectProjectBeforeStarting')}
        </div>
      )}

      {project && !selectedMode && (
        <div className={classes['errorMessageStatic']}>
          {t('selectModeToContiune')}
        </div>
      )}

      {showError && project && selectedMode && (
        <div className={classes['errorMessage']}>
          {selectedMode === 'oppgave' && !selectedActivity && t('selectTaskToContinue')}
          {selectedMode === 'maskin' && !machine && t('selectMachineToContinue')}
          {selectedMode === 'maskin' && machine && !selectedActivity && t('selectTaskToContinue')}
        </div>
      )}

      <div className={classes['selectionCard']}>
        <label className={classes['cardHeader']}>{t('project')} *</label>
        <div className={classes['inputWrapper']}>
          {isProjectReadonly && project ? (
            <div className={classes['readonlyField']}>
              {project.number || ''} {project.name || ''}
            </div>
          ) : (
            <ProjectSearchWrapper
              forceUpdate={forceUpdate}
              projectId={project?.id}
              onSelect={handleProjectSelect}
              t={t}
              classes={classes}
            />
          )}
        </div>
      </div>

      {project && (
        <div className={classes['modeSelection']}>
          <div className={classes['modeHeader']}>{t('selectOne')}</div>
          <div className={classes['modeCards']}>
            <div
              className={`${classes['modeCard']} ${!isModeReadonly && selectedMode === 'maskin' ? classes['modeCardActive'] : ''} ${isModeReadonly && selectedMode !== 'maskin' ? classes['modeCardDisabled'] : ''}`}
              onClick={() => !isModeReadonly && handleModeSelect('maskin')}
              style={isModeReadonly ? { cursor: 'default' } : {}}
            >
              <div className={classes['modeIcon']}>🔧</div>
              <div className={classes['modeTitle']}>{t('selectMachine')}</div>
              <div className={classes['modeSubtitle']}>{t('withDailyControl')}</div>
            </div>
            <div
              className={`${classes['modeCard']} ${!isModeReadonly && selectedMode === 'oppgave' ? classes['modeCardActive'] : ''} ${isModeReadonly ? classes['modeCardDisabled'] : ''}`}
              onClick={() => !isModeReadonly && handleModeSelect('oppgave')}
              style={isModeReadonly ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              <div className={classes['modeIcon']}>📄</div>
              <div className={classes['modeTitle']}>{t('selectTask')}</div>
              <div className={classes['modeSubtitle']}>{t('withoutMachine')}</div>
            </div>
            <div
              className={`${classes['modeCard']} ${!isModeReadonly && selectedMode === 'uspesifisert' ? classes['modeCardActive'] : ''} ${isModeReadonly ? classes['modeCardDisabled'] : ''}`}
              onClick={() => !isModeReadonly && handleModeSelect('uspesifisert')}
              style={isModeReadonly ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              <div className={classes['modeIcon']}>📊</div>
              <div className={classes['modeTitle']}>{t('unspecified')}</div>
              <div className={classes['modeSubtitle']}>{t('generalWork')}</div>
            </div>
          </div>
        </div>
      )}

      {selectedMode === 'maskin' && !machine && showMachineSearch && (
        <div className={classes['selectionCard']} key={`machine-section-${machineForceUpdate}`}>
          <div className={classes['cardHeader']}>{t('selectMachine')} *</div>
          <div className={classes.inputBox}>
            <MachineSearchWrapper
              machineForceUpdate={machineForceUpdate}
              selectedMode={selectedMode}
              onSelect={handleMachineSelect}
              t={t}
              classes={classes}
            />
          </div>
        </div>
      )}

      {selectedMode === 'maskin' && machine && (
        <>
          <div className={classes['machineCard']}>
            <div className={classes['machineCardHeader']}>
              <div className={classes['machineTitle']}>{t('machineName')}</div>
              {periodicChecks.length > 0 && completedChecksCount >= Math.min(minimumRequiredItems, periodicChecks.length) && (
                <div className={classes['controlBadge']}>
                  <span className={classes['checkIcon']}>✓</span> {t('controlCompleted')}
                </div>
              )}
            </div>
            <div className={classes['machineDetails']}>
              <div className={classes['machineName']}>
                {machine.intern} {machine.name}
              </div>
              {periodicCheckAwaitingSync && (
                <div className={classes['machineActivity']}>{tOffline('awaitingSynchronization')}</div>
              )}
              {selectedActivity && (
                <div className={classes['machineActivity']}>
                  {t('activity')}: {selectedActivity.name}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {selectedMode === 'oppgave' && selectedActivity && (
        <div className={classes['selectionCard']}>
          <div className={classes['cardHeader']}>{t('selectedTask')}</div>
          <div className={classes['cardContent']}>
            <span className={classes['cardValue']}>
              {selectedActivity.name}
            </span>
          </div>
        </div>
      )}

      {selectedMode === 'uspesifisert' && (
        <div className={classes['selectionCard']}>
          <div className={classes['cardHeader']}>{t('workMode')}</div>
          <div className={classes['cardContent']}>
            <span className={classes['cardValue']}>{t('unspecified')}</span>
          </div>
          {unspecifiedNotes && (
            <div className={classes['cardContent']}>
              <span className={classes['cardValue']}>{unspecifiedNotes}</span>
            </div>
          )}
        </div>
      )}

      <div className={classes['selectionCard']}>
        <div className={classes['cardHeader']}>{t('user')}</div>
        <div className={classes['cardContent']}>
          <span className={classes['cardValue']}>
            {currentUserName || 'Ole Hansen'}
          </span>
        </div>
      </div>

      <button
        onClick={handleTimeRegistration}
        className={classes['timeButton']}
        disabled={
          !project ||
          !selectedMode ||
          (selectedMode === 'oppgave' && !selectedActivity) ||
          (selectedMode === 'maskin' && (!machine || !selectedActivity)) ||
          (selectedMode === 'uspesifisert' && false)
        }
      >
        {t('timeRegistration')}
      </button>

      {showUnspecifiedModal && (
        <div className={classes['modalOverlay']} onClick={() => setShowUnspecifiedModal(false)}>
          <div className={classes['modalContent']} onClick={(e) => e.stopPropagation()}>
            <div className={classes['modalHeader']}>
              <h3 className={classes['modalTitle']}>{t('unspecified')}</h3>
              <button className={classes['closeButton']} onClick={() => setShowUnspecifiedModal(false)}>×</button>
            </div>
            <div style={{ padding: '16px' }}>
              <textarea
                value={unspecifiedNotes}
                onChange={(e) => setUnspecifiedNotes(e.target.value)}
                placeholder={t('descriptionPlaceholder') || 'Description...'}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className={classes['modalFooter']}>
              <button
                className={classes['selectButton']}
                onClick={() => setShowUnspecifiedModal(false)}
              >
                {t('select')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showChecksModal && (
        <div className={classes['modalOverlay']} onClick={closeChecksModal}>
          <div className={classes['modalContent']} onClick={(e) => e.stopPropagation()}>
            <div className={classes['modalHeader']}>
              <h3 className={classes['modalTitle']}>
                {t('periodicChecks')}
              </h3>
              <button className={classes['closeButton']} onClick={closeChecksModal}>
                ×
              </button>
            </div>

            <div className={classes['checksModalBody']}>
              {loadingChecks ? (
                <div className={classes['loadingMessage']}>{t('loadingChecks')}</div>
              ) : periodicChecks.length === 0 ? (
                <>
                  <div className={classes['emptyMessage']}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                      {t('noChecksAvailable') || 'No periodic checks available'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {machine && (
                    <div className={classes['machineHeader']}>
                      <div className={classes['machineLabel']}>{t('machine')}</div>
                      <div className={classes['machineNameDisplay']}>
                        {machine.name} - {machine.intern}
                      </div>
                      {periodicCheckAwaitingSync && (
                        <div className={classes['machineActivity']}>{tOffline('awaitingSynchronization')}</div>
                      )}
                    </div>
                  )}

                  <div className={completedChecksCount >= Math.min(minimumRequiredItems, periodicChecks.length) ? classes['requiredWarningComplete'] : classes['requiredWarning']}>
                    <div className={classes['warningIcon']}>
                      {completedChecksCount >= Math.min(minimumRequiredItems, periodicChecks.length) ? '✓' : '⚠'}
                    </div>
                    <div className={classes['warningContent']}>
                      <div className={classes['warningTitle']}>
                        {completedChecksCount >= Math.min(minimumRequiredItems, periodicChecks.length) ? t('checkComplete') : t('controlMachine')}
                      </div>
                      <div className={classes['warningText']}>
                        {completedChecksCount} {t('of')} {periodicChecks.length} {t('pointsChecked')} ({t('min')}. {Math.min(minimumRequiredItems, periodicChecks.length)} {t('required')})
                      </div>
                    </div>
                  </div>
                  <div className={classes['checklistTitle']}>{t('checklist')}</div>
                  <div className={classes['checksList']}>
                    {periodicChecks.map((check, index) => {
                      const showPhotoLink = index === 0;

                      return (
                        <div key={check.id}>
                          <div
                            className={`${classes['checkItemRow']} ${check.status === 1 ? classes['checkItemRowSelected'] : ''}`}
                          >
                            <div className={classes['checkItemLeft']}>
                              <span className={classes['checkNameText']}>
                                {check.name}
                                {check.isRequired && <span className={classes['required']}> *</span>}
                              </span>
                            </div>

                            <div className={classes['checkItemActions']}>
                              <div style={{ position: 'relative' }}>
                                <Icon
                                  name={'image'}
                                  className={`${classes['actionIcon']} ${classes['imageIcon']} ${(check?.images?.length || 0) > 0 ? classes['attached'] : ''}`}
                                  onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleImageUpload(check.id);
                                  }}
                                />
                                {(check?.images?.length || 0) > 0 && (
                                  <span className={classes['imageCount']}>{check.images?.length}</span>
                                )}
                              </div>
                              {!check.isRequired && (
                                <button
                                  type="button"
                                  className={`${classes['actionIcon']} ${classes['naBtn']} ${check.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE ? classes['checked'] : ''}`}
                                  onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleStatusChange(check.id, PERIODIC_CHECK_ITEM_NOT_APPLICABLE);
                                  }}
                                >
                                  {t('notApplicable')}
                                </button>
                              )}
                              <Icon
                                name={'close'}
                                className={`${classes['actionIcon']} ${classes['cancelIcon']} ${check.status === 2 ? classes['checked'] : ''}`}
                                onClick={(e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStatusChange(check.id, 2);
                                }}
                              />
                              <Icon
                                name={'done'}
                                className={`${classes['actionIcon']} ${classes['okIcon']} ${check.status === 1 ? classes['checked'] : ''}`}
                                onClick={(e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStatusChange(check.id, 1);
                                }}
                              />
                            </div>
                          </div>
                          {showPhotoLink && (
                            <div className={classes['photoUploadLink']}>
                              <Icon name={'camera_alt'} className={classes['cameraIcon']} />
                              <span
                                className={classes['photoLinkText']}
                                onClick={() => handleImageUpload(check.id)}
                              >
                                {t('takeOrUploadPhotos') || 'Take or upload a photos'}
                              </span>
                              <span className={classes['photoLimit']}>
                                {t('upTo3Pics') || 'Up to 3 pcs.'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className={classes['modalFooter']}>
              {!periodicCheckPermission && periodicChecks.length > 0 ? (
                <>
                  <button
                    className={classes['selectButton']}
                    onClick={handleChecksConfirm}
                    disabled={completedChecksCount < Math.min(minimumRequiredItems, periodicChecks.length)}
                  >
                    {t('continue')}
                  </button>
                  <button
                    className={classes['selectButton']}
                    onClick={async () => {
                      setShowChecksModal(false);
                      await fetchActivities('maskin');
                      if (!isMountedRef.current) return;
                      setShowActivityModal(true);
                    }}
                  >
                    {t('skip') || 'Skip'}
                  </button>
                </>
              ) : periodicChecks.length === 0 ? (
                <button
                  className={classes['selectButton']}
                  onClick={async () => {
                    setShowChecksModal(false);
                    await fetchActivities();
                    if (!isMountedRef.current) return;
                    setShowActivityModal(true);
                  }}
                >
                  {t('skip') || 'Skip'}
                </button>
              ) : (
                <button
                  className={classes['selectButton']}
                  onClick={handleChecksConfirm}
                  disabled={completedChecksCount < Math.min(minimumRequiredItems, periodicChecks.length)}
                >
                  {t('continue')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showActivityModal && (
        <div className={classes['modalOverlay']} onClick={closeActivityModal}>
          <div className={classes['modalContent']} onClick={(e) => e.stopPropagation()}>
            <div className={classes['modalHeader']}>
              <h3 className={classes['modalTitle']}>{t('selectTask')}</h3>
              <button className={classes['closeButton']} onClick={closeActivityModal}>
                ×
              </button>
            </div>

            <div className={classes['activityList']}>
              {loadingActivities ? (
                <div className={classes['loadingMessage']}>{t('loading')}</div>
              ) : activities.length === 0 ? (
                <div className={classes['emptyMessage']}>{t('noTasksFound')}</div>
              ) : (
                <div className={classes['activityPills']}>
                  {activities.map((activity) => (
                    <button
                      key={activity.id}
                      className={`${classes['activityPill']} ${selectedActivity?.id === activity.id ? classes['activityPillActive'] : ''}`}
                      onClick={() => handleActivitySelect(activity)}
                    >
                      {activity.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={classes['modalFooter']}>
              <button
                className={classes['selectButton']}
                onClick={closeActivityModal}
                disabled={!selectedActivity}
              >
                {t('select')}
              </button>
            </div>
          </div>
        </div>
      )}
      <AddIssue
        open={openAddIssueDialog}
        onClose={handleAddIssueClose}
        type="issue"
        inspectionType={checksData?.inspectionType}
        issueDetails={issueDetailsForDialog}
        sourceTypeId={SOURCE_ISSUE_TYPE.DAILY_CHECK}
      />
      {!showProjectSelection && (oppgaverPermission || (hasTimeforingModule && timeManagementPermission)) &&
        <div className={classes['bottomNav']}>
          <Button className={classes['bottomButton']} to={route(PROJECTS_ROUTES.list)}>
            <Icon name={'home' as Icons} className={classes['bottomIcon']} />
            {tb('home')}
          </Button>
          {hasTimeforingModule && (
            <AccessControl requiredPermissions={PERMISSION_GROUPS.timeManagement}>
              <Button
                className={classes['bottomButton']}
                to={route(TIME_MANAGEMENT_ROUTES.list)}
              >
                <Icon name={'schedule' as Icons} className={classes['bottomIcon']} />
                {tb('timeManagement')}
              </Button>
            </AccessControl>
          )}
          {oppgaverPermission && <Button className={classes['bottomButton']} to={route(TASK_ROUTES.list)}>
            <Icon name={'assignment' as Icons} className={classes['bottomIcon']} />
            {tb('task')}
          </Button>}
        </div>
      }
    </>
  );
}

export default List;